import { createContext, FC, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { usePagination, UsePaginationOutput } from '@/support/Hooks/api/usePagination';
import { StudyApiId, StudyInfoV1, StudyType } from '@/model/Study.model';
import InVivoError from '@/model/InVivoError';
import { type CalendarFilters, type DateRange, type StudyColours } from '@/components/Tasks/Calendar/Calendar.model';
import { useCache, UseCacheResponse } from '@/support/Hooks/api/useCache';
import { _notNil, uuid } from '@/littledash';
import { TaskV1 } from '@/model/Task.model';
import { retry } from '@/components/Tasks/Calendar/Views/Day/GanttView.utils';
import type { Endpoints } from '@/generated/internal-api/Endpoints';
import { generateIntegrity } from '@/helpers';
import { createSelector } from '@reduxjs/toolkit';
import { currentUserSelector } from '@/support/Selectors';
import { DateUtils } from '@/utils/Date.utils';
import { useSelector } from 'react-redux';
import { ALLOWED_STUDY_STATUSES } from '../../Calendar.utils';

export type GanttViewContext = {
  props: GanttViewProps;
  filterIntegrity: string | null;
  loadingStudies: boolean;
  pagination: UsePaginationOutput<StudyApiId, StudyInfoV1>;
  studyInfoCache: UseCacheResponse<StudyApiId, StudyInfoV1>;
  tasksCache: UseCacheResponse<StudyApiId, Array<TaskV1>>;
  userTimezone: string;
  taskStudies: Set<StudyApiId>;
};

type FetchTasksQueryParameters = Endpoints['GET /api/v1/tasks']['query'] & {
  study_status?: StudyType[];
};
type GanttViewProps = {
  dateRange: DateRange;
  filters: CalendarFilters;
  studyColours: StudyColours;
  narrow?: boolean;
};
const userTimezoneSelector = createSelector(
  [currentUserSelector],
  (user) => user?.timezone ?? DateUtils.browserTimezone()
);

export const GanttViewContext = createContext<GanttViewContext>({} as GanttViewContext);

export const GanttViewProvider: FC<PropsWithChildren<GanttViewProps>> = ({ children, ...props }) => {
  const userTimezone = useSelector(userTimezoneSelector);
  const [filterIntegrity, updateFilterIntegrity] = useState<string>('initial');

  const { invoke: loadStudies, loading: loadingStudies } = useApiHook({
    endpoint: 'GET /api/v1/studies',
    options: { onError: { slug: 'gantt-study-list' } },
    invokeOnInit: false,
  });

  const { invoke: loadStudyInfo } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/info',
    options: { onError: { slug: 'gantt-study-info-get', toast: false, throw: false } },
    invokeOnInit: false,
  });

  const { invoke: fetchTasks } = useApiHook({
    endpoint: 'GET /api/v1/tasks',
    query: {},
    invokeOnInit: false,
    options: { onError: { slug: 'gantt-study-tasks', toast: false, capture: false, throw: true } },
  });

  const { invoke: fetchTaskStudies, response: taskStudiesResponse } = useApiHook({
    endpoint: 'GET /api/v1/tasks/studies',
    invokeOnInit: false,
    options: { onError: { slug: 'gantt-task-studies', toast: false, capture: false, throw: true } },
  });

  const taskStudies = useMemo(() => new Set(taskStudiesResponse?.body ?? []), [taskStudiesResponse]);

  const pagination = usePagination<StudyApiId, StudyInfoV1>({
    query: (page: number) =>
      loadStudies({ query: { page, perPage: 50 } }).then((response) => {
        if (response.type === 'success') {
          return {
            data: response.body.data,
            state: {
              total: response.body.meta.total ?? 0,
              current_page: response.body.meta.current_page ?? 0,
              last_page: response.body.meta.last_page ?? 0,
            },
          };
        }
        throw new InVivoError('Could not load studies', { cause: response.error, slug: 'paginated-study-load' });
      }),
    key: (item) => item.api_id,
  });
  const studyInfoCache = useCache<StudyApiId, StudyInfoV1>((key) => {
    const value = pagination.items.get(key);
    if (_notNil(value)) {
      return Promise.resolve({ key, value, cache: false });
    }
    return loadStudyInfo({ path: { studyApiId: key } }).then((response) => {
      return response.type === 'success'
        ? { key: response.body.api_id, value: response.body, cache: true }
        : Promise.reject(response.error);
    });
  });

  const refreshTaskStudies = useCallback(
    () =>
      fetchTaskStudies({
        query: {
          from: props.dateRange.start,
          to: props.dateRange.end,
          assignee: props.filters.assignee,
          study_status: ALLOWED_STUDY_STATUSES,
          status: props.filters.status,
          task_range: 'overlap',
        },
      }),
    [fetchTaskStudies, props.filters]
  );

  const tasksCache = useCache<StudyApiId, Array<TaskV1>>(async (key) => {
    const baseQueryParameters: FetchTasksQueryParameters = {
      assignee: props.filters.assignee,
      status: props.filters.status,
      study_api_id: [key],
      study_status: ALLOWED_STUDY_STATUSES,
      sort: 'start',
      order: 'asc',
      perPage: 50,
      include: ['target_animal_count'],
      start: props.dateRange.start,
      end: props.dateRange.end,
      task_range: 'overlap',
    };

    const pageOne = await retry(() => fetchTasks({ query: { ...baseQueryParameters, page: 1 } }));
    if (pageOne.type === 'error') {
      return Promise.reject(pageOne.error);
    }

    const value = await Promise.allSettled([
      Promise.resolve(pageOne),
      ...Array.from({ length: (pageOne.body.meta.last_page ?? 1) - 1 }).map((_, idx) =>
        retry(() => fetchTasks({ query: { ...baseQueryParameters, page: idx + 2 } }))
      ),
    ]).then((pageResponses) =>
      pageResponses.reduce<Array<TaskV1>>(
        (acc, pageResponse) =>
          pageResponse.status === 'fulfilled' && pageResponse.value.type === 'success'
            ? [...acc, ...pageResponse.value.body.data]
            : acc,
        []
      )
    );
    return { key, value, cache: true };
  });

  useEffect(() => {
    generateIntegrity(JSON.stringify({ ...(props.dateRange ?? {}), ...(props.filters ?? {}) }))
      .then((integrity) => {
        updateFilterIntegrity((prev) => (integrity !== prev ? integrity : prev));
      })
      .catch(() => updateFilterIntegrity(uuid()));
  }, [props.filters, props.dateRange]);

  useEffect(() => {
    tasksCache.reset();
    refreshTaskStudies();
  }, [filterIntegrity]);

  return (
    <GanttViewContext.Provider
      value={{
        loadingStudies,
        filterIntegrity,
        props,
        pagination,
        studyInfoCache,
        tasksCache,
        userTimezone,
        taskStudies,
      }}
    >
      {children}
    </GanttViewContext.Provider>
  );
};
